import { putOSS } from '@/utils/uploadOSS.js';
import store from '@/store';
import Data from '@/utils/data.js';

import stampAgree from '@/assets/report/stamp_agree.png';
import stampReject from '@/assets/report/stamp_reject.png';
import stampProcess from '@/assets/report/stamp_process.png';
import stampClose from '@/assets/report/stamp_close.png';

// 解析接口返回的文件字段
export function parseReportFiles(file) {
    if (!file) {
        return [];
    }
    if (Array.isArray(file)) {
        return file.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            return item.url || item.path || item.file || '';
        }).filter(Boolean);
    }
    if (typeof file === 'string') {
        const trimmed = file.trim();
        if (!trimmed || trimmed === '""' || trimmed === '[]') {
            return [];
        }
        try {
            const parsed = JSON.parse(trimmed);
            return parseReportFiles(parsed);
        } catch (err) {
            return trimmed.indexOf('http') === 0 ? [trimmed] : [];
        }
    }
    if (typeof file === 'object') {
        const values = Object.keys(file)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => file[key]);
        return values.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            if (item && typeof item === 'object') {
                return item.url || item.path || item.file || '';
            }
            return '';
        }).filter(Boolean);
    }
    return [];
}

export function formatReportFiles(urls) {
    const validUrls = (urls || []).filter((url) => typeof url === 'string' && url.trim());
    if (!validUrls.length) {
        return '';
    }
    return JSON.stringify(validUrls.map((url) => ({ url })));
}

// 上传本地图片（支持 File / van-uploader 对象 / 已上传 url）
export function uploadReportImages(localFiles) {
    const tasks = (localFiles || []).map((item) => {
        if (typeof item === 'string') {
            if (/^https?:\/\//.test(item)) {
                return Promise.resolve(item);
            }
            return Promise.reject(new Error('无效图片'));
        }
        if (item && typeof item === 'object') {
            if (item.url && /^https?:\/\//.test(item.url) && !item.file) {
                return Promise.resolve(item.url);
            }
            const file = item.file || item;
            if (file instanceof File || file instanceof Blob) {
                return putOSS(file);
            }
        }
        if (item instanceof File || item instanceof Blob) {
            return putOSS(item);
        }
        return Promise.reject(new Error('无效图片'));
    });
    return Promise.all(tasks);
}

export const REPORT_TYPE_NAME_MAP = {
    cannot_contact_owner: '无法联系车主',
    change_rescue_type: '修改救援类型',
    car_in_basement: '车在地库',
    add_wheel: '增加小轮',
    change_to_online: '改为线上案件',
    change_to_offline: '改为线下案件',
    other: '其它情况',
    no_car_license: '没行驶证',
    no_driver_license: '没驾驶证',
    dashboard_not_light: '仪表盘不亮',
    add_rescue_type: '增补救援类型',
    modified_arrive_time: '修改预计到达时间',
    price_negotiation: '议价',
    add_basement_rescue: '车在地库'
};

export function resolveReportTypeLabel(typeValue, typeMap) {
    if (!typeValue) {
        return '';
    }
    if (typeMap && typeMap[typeValue]) {
        return typeMap[typeValue];
    }
    return REPORT_TYPE_NAME_MAP[typeValue] || typeValue;
}

function buildTypeMap(typeOptions) {
    const map = {};
    (typeOptions || []).forEach((item) => {
        if (item && item.value) {
            map[item.value] = item.label || item.name || item.value;
        }
    });
    return map;
}

export function isTypeOperableValue(operable) {
    return !(operable === false || operable === 0 || operable === '0' || operable === 'false');
}

function parseReportTypeItem(item, fallbackValue) {
    if (item == null) {
        return null;
    }
    if (typeof item === 'string' || typeof item === 'number') {
        const value = fallbackValue || String(item);
        return {
            label: String(item),
            value,
            operable: true,
            notice: ''
        };
    }
    if (typeof item !== 'object') {
        return null;
    }
    const rawLabel = item.name || item.label || item.title || item.text || item.type_name || '';
    const label = typeof rawLabel === 'string' ? rawLabel : '';
    const value = fallbackValue || item.type || item.code || item.key || item.value || item.id || '';
    if (!value) {
        return null;
    }
    return {
        label: label || REPORT_TYPE_NAME_MAP[value] || String(value),
        value: String(value),
        operable: isTypeOperableValue(item.operable),
        notice: typeof item.notice === 'string' ? item.notice : (item.notice || item.message || '')
    };
}

export function normalizeReportTypes(res) {
    const data = res && res.data;
    let types = [];
    if (Array.isArray(data)) {
        types = data.map((item) => parseReportTypeItem(item)).filter(Boolean);
    } else if (data && Array.isArray(data.list)) {
        types = data.list.map((item) => parseReportTypeItem(item)).filter(Boolean);
    } else if (data && Array.isArray(data.type)) {
        types = data.type.map((item) => parseReportTypeItem(item)).filter(Boolean);
    } else if (data && Array.isArray(data.data)) {
        types = data.data.map((item) => parseReportTypeItem(item)).filter(Boolean);
    } else if (data && typeof data === 'object') {
        types = Object.keys(data).map((key) => parseReportTypeItem(data[key], key)).filter(Boolean);
    }
    return types;
}

function getReportContentList(item) {
    const content = item && item.content;
    if (!Array.isArray(content)) {
        return [];
    }
    return [...content].sort((a, b) => {
        const timeA = new Date(a.create_time || 0).getTime();
        const timeB = new Date(b.create_time || 0).getTime();
        if (timeA !== timeB) {
            return timeA - timeB;
        }
        return Number(a.id || 0) - Number(b.id || 0);
    });
}

function getMessageSender(item) {
    const user = (item && item.user) || {};
    const relate = user.relate || {};
    const relateType = Number(user.relate_type);
    const isDriver = relateType === 2;
    const name = relate.name || relate.nick || relate.account || '';
    const displayName = name || (isDriver ? '我' : '调度员');
    return {
        isDriver,
        role: isDriver ? 'me' : 'dispatcher',
        roleName: isDriver ? '我' : displayName,
        userName: isDriver ? '我' : displayName,
        roleLabel: isDriver ? '我' : '调度',
        userId: user.id || user.relate_id || '',
        avatarText: isDriver ? '我' : (displayName ? displayName.charAt(0) : '调')
    };
}

const SCALAR_TYPE_MAP = {
    add_basement_rescue: (val) => `地库${val}层`,
    car_in_basement: (val) => `地库${val}层`,
    add_wheel: (val) => `小轮${val}个`,
    price_negotiation: (val) => `${val}元`,
    change_rescue_type: (val, typeConfigList) => resolveRescueTypeName(val, typeConfigList),
    add_rescue_type: (val, typeConfigList) => resolveRescueTypeName(val, typeConfigList),
    modified_arrive_time: (val) => `预计${val}分钟`
};

/** 从 typeConfig 列表中查找救援类型名称 */
function findRescueTypeTitle(list, val) {
    if (!list) {
        return '';
    }
    const target = String(val);
    const arr = Array.isArray(list)
        ? list
        : (typeof list === 'object' ? Object.keys(list).map((key) => list[key]) : []);
    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        if (!item || typeof item !== 'object') {
            continue;
        }
        const no = item.no !== undefined ? item.no : (item.id !== undefined ? item.id : item.type);
        if (String(no) === target) {
            return item.title || item.short || item.name || '';
        }
    }
    return '';
}

/** 救援类型 no -> 名称（修改救援类型 scalar 反显） */
export function resolveRescueTypeName(val, typeConfigList) {
    if (val === undefined || val === null || val === '') {
        return '';
    }
    let title = findRescueTypeTitle(typeConfigList, val);
    if (title) {
        return title;
    }
    try {
        title = findRescueTypeTitle(store && store.state && store.state.typeDataConfig, val);
        if (title) {
            return title;
        }
    } catch (err) { /* ignore */ }
    const fallback = Data.rescueType && (Data.rescueType[val] || Data.rescueType[String(val)] || Data.rescueType[Number(val)]);
    if (fallback) {
        return fallback;
    }
    return String(val);
}

/** 解析消息 extra，取出 scalar */
export function parseReportExtraScalar(extra) {
    if (extra === undefined || extra === null || extra === '') {
        return null;
    }
    let data = extra;
    if (typeof data === 'string') {
        const trimmed = data.trim();
        if (!trimmed) {
            return null;
        }
        try {
            data = JSON.parse(trimmed);
        } catch (err) {
            return trimmed;
        }
    }
    if (typeof data === 'object') {
        if (data.scalar !== undefined && data.scalar !== null && data.scalar !== '') {
            return data.scalar;
        }
        return null;
    }
    return data;
}

export function isChangeRescueType(typeValue, typeLabel) {
    const value = String(typeValue || '');
    const label = String(typeLabel || '');
    return value === 'change_rescue_type'
        || value === 'add_rescue_type'
        || label.indexOf('修改救援类型') >= 0
        || label.indexOf('增补救援类型') >= 0;
}

export function isArriveTimeType(typeValue, typeLabel) {
    const value = String(typeValue || '');
    const label = String(typeLabel || '');
    return value === 'modified_arrive_time'
        || label.indexOf('修改预计到达时间') >= 0;
}

export function getScalarDisplay(typeValue, scalar, typeConfigList) {
    if (scalar === undefined || scalar === null || scalar === '') {
        return null;
    }
    const value = String(typeValue || '');
    if (isChangeRescueType(value, value)) {
        return {
            text: resolveRescueTypeName(scalar, typeConfigList),
            showAgree: false
        };
    }
    if (isArriveTimeType(value, value)) {
        return {
            text: `预计${scalar}分钟`,
            showAgree: false
        };
    }
    const formatter = SCALAR_TYPE_MAP[value];
    if (!formatter) {
        return null;
    }
    return {
        text: typeof formatter === 'function' ? formatter(scalar, typeConfigList) : formatter,
        showAgree: value === 'price_negotiation'
    };
}

function mapContentMessage(item) {
    const sender = getMessageSender(item);
    return {
        id: item.id,
        contentId: item.id,
        role: sender.role,
        roleName: sender.roleName,
        userName: sender.userName,
        roleLabel: sender.roleLabel,
        userId: sender.userId,
        avatarText: sender.avatarText,
        isDriver: sender.isDriver,
        content: item.content || '',
        images: parseReportFiles(item.file),
        extra: item.extra || null,
        time: item.create_time || ''
    };
}

export const REPORT_STATUS_MAP = {
    1: { label: '同意', className: 'stamp_agree', stampImg: stampAgree },
    2: { label: '拒绝', className: 'stamp_reject', stampImg: stampReject },
    3: { label: '处理', className: 'stamp_process', stampImg: stampProcess },
    4: { label: '关闭', className: 'stamp_close', stampImg: stampClose }
};

export function getReportStatusStamp(status) {
    const key = Number(status);
    return REPORT_STATUS_MAP[key] || null;
}

function unwrapRawItem(item) {
    if (!item) {
        return null;
    }
    if (Array.isArray(item.content)) {
        return item;
    }
    if (item.raw && Array.isArray(item.raw.content)) {
        return item.raw;
    }
    return item;
}

function mapReportListItem(item, typeMap) {
    const rawItem = unwrapRawItem(item) || item;
    const contentList = getReportContentList(rawItem);
    const firstMsg = contentList[0];
    const typeValue = rawItem.type || rawItem.type_value || '';
    const messages = contentList.map(mapContentMessage);
    return {
        id: rawItem.id || rawItem.report_id,
        type: resolveReportTypeLabel(typeValue, typeMap) || typeValue,
        typeValue,
        time: rawItem.create_time || (firstMsg && firstMsg.create_time) || '',
        remark: firstMsg ? (firstMsg.content || '') : '',
        images: firstMsg ? parseReportFiles(firstMsg.file) : [],
        messages,
        replies: messages.slice(1),
        extra: rawItem.extra || null,
        status: rawItem.status,
        is_done: rawItem.is_done,
        result: rawItem.result || null,
        unread: Number(rawItem.unread) || 0,
        raw: rawItem
    };
}

function pickReportItem(data, fallback, targetId) {
    const id = targetId || (fallback && (fallback.id || fallback.report_id));
    if (!data) {
        return unwrapRawItem(fallback);
    }
    let list = null;
    if (Array.isArray(data)) {
        list = data;
    } else if (data && Array.isArray(data.list)) {
        list = data.list;
    } else if (data && Array.isArray(data.data)) {
        list = data.data;
    }
    if (Array.isArray(list)) {
        if (id) {
            return list.find((item) => item.id == id) || list[0] || unwrapRawItem(fallback);
        }
        return list[0] || unwrapRawItem(fallback);
    }
    if (typeof data === 'object' && (data.id || data.report_id)) {
        return data;
    }
    return unwrapRawItem(fallback);
}

export function normalizeReportList(res, typeOptions) {
    const typeMap = buildTypeMap(typeOptions);
    const data = res && res.data;
    if (Array.isArray(data)) {
        return data.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && Array.isArray(data.list)) {
        return data.list.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && Array.isArray(data.data)) {
        return data.data.map((item) => mapReportListItem(item, typeMap));
    }
    if (data && typeof data === 'object' && data.id) {
        return [mapReportListItem(data, typeMap)];
    }
    return [];
}

export function normalizeReportDetail(res, fallback, typeOptions) {
    const typeMap = buildTypeMap(typeOptions);
    const fallbackRaw = unwrapRawItem(fallback);
    const targetId = fallbackRaw && (fallbackRaw.id || fallbackRaw.report_id);
    const item = pickReportItem(res && res.data, fallbackRaw, targetId);
    if (!item) {
        return fallback && fallback.messages ? fallback : (fallback || {});
    }
    const normalized = mapReportListItem(item, typeMap);
    if (!normalized.messages.length && fallback && Array.isArray(fallback.messages) && fallback.messages.length) {
        return {
            ...fallback,
            ...normalized,
            messages: fallback.messages,
            replies: fallback.replies,
            remark: fallback.remark,
            images: fallback.images
        };
    }
    return normalized;
}
